import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListUsers,
  useListSpaces,
  useInviteUser,
  useUpdateUser,
  useSetUserActive,
  useGetStaffAttendanceDay,
  useLogoutAttendanceSession,
  getListUsersQueryKey,
  getListSpacesQueryKey,
  getGetStaffAttendanceDayQueryKey,
  type User,
  type UserRole,
  type StaffAttendanceDayEntry,
} from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { normalizePhone, isTenDigits, PHONE_HELP } from "@/lib/phone";
import { fmtTime, fmtDuration } from "@/components/attendance/attendance-list";
import { Loader2, LogOut, Plus } from "lucide-react";

const ROLE_LABEL: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Centralised Admin",
  centralised_team: "Centralised Team",
  agent: "Agent",
  ops_lead: "Ops Lead",
  front_desk: "Front Desk",
  housekeeping: "Housekeeping",
  bde: "BDE",
};

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Centralised Admin" },
  { value: "centralised_team", label: "Centralised Team" },
  { value: "agent", label: "Agent" },
  // Phase 41 (TMIG-04) task roles. In lockstep with USER_ROLES in
  // lib/db/src/schema/users.ts, the users_role_check DB CHECK (migration 0106),
  // and openapi.yaml's UserRole enum.
  { value: "ops_lead", label: "Ops Lead" },
  { value: "front_desk", label: "Front Desk" },
  { value: "housekeeping", label: "Housekeeping" },
  { value: "bde", label: "BDE" },
];

// Mirrors requiresSpace() in artifacts/api-server/src/routes/users.ts. A non-admin
// with a null spaceId is scoped to nothing by spaceScope(), so they'd sign in to an
// empty CRM. Every task role is location staff.
const SPACE_REQUIRED_ROLES = new Set<UserRole>([
  "agent",
  "centralised_team",
  "ops_lead",
  "front_desk",
  "housekeeping",
  "bde",
]);

const NO_SPACE = "__none__";

// ── attendance (IST) ────────────────────────────────────────────────────────
const IST_OFFSET = "+05:30";

function todayIst(): string {
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}
/** ISO instant → "YYYY-MM-DDTHH:MM" for a datetime-local input, in IST. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  return new Date(t + 5.5 * 3600 * 1000).toISOString().slice(0, 16);
}
/** datetime-local value (IST wall clock) → ISO instant. */
function localInputToISO(v: string): string | null {
  if (!v) return null;
  const d = new Date(`${v}:00${IST_OFFSET}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
/** Minutes between two instants, floored at 0. */
function minutesBetween(fromISO: string | null | undefined, toMs: number): number {
  if (!fromISO) return 0;
  const from = new Date(fromISO).getTime();
  if (Number.isNaN(from)) return 0;
  return Math.max(0, Math.round((toMs - from) / 60_000));
}
/**
 * Hours to show for the day: whatever is already banked from closed sessions,
 * plus the running clock of an open one. Mirrors the server's arithmetic
 * (POST /attendance/sessions/:id/logout) so the number doesn't jump on save.
 */
function dayMinutes(entry: StaffAttendanceDayEntry, nowMs: number): number | null {
  const banked = entry.durationMinutes ?? 0;
  if (!entry.loggedIn) return entry.durationMinutes ?? null;
  return banked + minutesBetween(entry.openEntryAt, nowMs);
}

function spaceLabel(spaceId: number | null, spaces: { id: number; name: string }[] | undefined) {
  if (spaceId == null) return "—";
  return spaces?.find((s) => s.id === spaceId)?.name ?? `Space #${spaceId}`;
}

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    // ApiError carries the server's { error } body on .data — prefer it, so the
    // toast reads "Already logged out" instead of "HTTP 400 Bad Request: …".
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const serverError = (data as { error?: unknown }).error;
      if (typeof serverError === "string" && serverError.trim()) return serverError;
    }
    if ("message" in err) {
      const msg = (err as { message?: unknown }).message;
      if (typeof msg === "string" && msg.trim()) return msg;
    }
  }
  return fallback;
}

export default function Team() {
  const { user: me, isLoading: meLoading } = useAuth();
  const [, setLocation] = useLocation();
  const isAdmin = me?.role === "super_admin" || me?.role === "admin";
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (!meLoading && me && !isAdmin) {
      setLocation("/");
    }
  }, [meLoading, me, isAdmin, setLocation]);

  const { data: users, isLoading: usersLoading } = useListUsers({
    query: { queryKey: getListUsersQueryKey(), enabled: isAdmin },
  });
  const { data: spaces, isLoading: spacesLoading } = useListSpaces({
    query: { queryKey: getListSpacesQueryKey(), enabled: isAdmin },
  });

  // Today's staff attendance: the biometric punch opens the login by itself, so
  // this is read-only except for the manual Logout below.
  const [attendanceDate] = useState(todayIst);
  const attendanceParams = { date: attendanceDate };
  const { data: attendance } = useGetStaffAttendanceDay(attendanceParams, {
    query: { queryKey: getGetStaffAttendanceDayQueryKey(attendanceParams), enabled: isAdmin },
  });
  const attendanceByUser = useMemo(() => {
    const m = new Map<number, StaffAttendanceDayEntry>();
    for (const a of attendance ?? []) m.set(a.userId, a);
    return m;
  }, [attendance]);

  // Ticking clock so the running hours of anyone still on the clock advance.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const someoneOnTheClock = (attendance ?? []).some((a) => a.loggedIn);
  useEffect(() => {
    if (!someoneOnTheClock) return;
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [someoneOnTheClock]);

  const [loggingOut, setLoggingOut] = useState<{ user: User; entry: StaffAttendanceDayEntry } | null>(
    null,
  );

  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteNumber, setInviteNumber] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>("agent");
  const [inviteSpaceId, setInviteSpaceId] = useState<string>(NO_SPACE);
  const [inviteBiometricId, setInviteBiometricId] = useState("");
  const [inviteBiometricSpaceId, setInviteBiometricSpaceId] = useState<string>(NO_SPACE);

  const [editing, setEditing] = useState<User | null>(null);
  const [editName, setEditName] = useState("");
  const [editNumber, setEditNumber] = useState("");
  const [editRole, setEditRole] = useState<UserRole>("agent");
  const [editSpaceId, setEditSpaceId] = useState<string>(NO_SPACE);
  const [editBiometricId, setEditBiometricId] = useState("");
  const [editBiometricSpaceId, setEditBiometricSpaceId] = useState<string>(NO_SPACE);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });

  const refreshAttendance = () =>
    queryClient.invalidateQueries({
      queryKey: getGetStaffAttendanceDayQueryKey(attendanceParams),
    });

  const invite = useInviteUser({
    mutation: {
      onSuccess: () => {
        toast({ title: "Team member invited" });
        setShowInvite(false);
        setInviteName("");
        setInviteNumber("");
        setInviteRole("agent");
        setInviteSpaceId(NO_SPACE);
        setInviteBiometricId("");
        setInviteBiometricSpaceId(NO_SPACE);
        void refresh();
      },
      onError: (err) =>
        toast({
          title: "Could not invite member",
          description: errorMessage(err, "Please try again."),
          variant: "destructive",
        }),
    },
  });

  const update = useUpdateUser({
    mutation: {
      onSuccess: () => {
        toast({ title: "Member updated" });
        setEditing(null);
        void refresh();
      },
      onError: (err) =>
        toast({
          title: "Could not update member",
          description: errorMessage(err, "Please try again."),
          variant: "destructive",
        }),
    },
  });

  const setActive = useSetUserActive({
    mutation: {
      onSuccess: (updated) => {
        toast({ title: updated.active ? "Member enabled" : "Member disabled" });
        void refresh();
      },
      onError: (err) =>
        toast({
          title: "Could not change access",
          description: errorMessage(err, "Please try again."),
          variant: "destructive",
        }),
    },
  });

  const logout = useLogoutAttendanceSession({
    mutation: {
      onSuccess: (session) => {
        toast({
          title: "Logged out",
          description: `${session.userName ?? "Team member"} — ${fmtDuration(session.durationMinutes)} counted.`,
        });
        setLoggingOut(null);
        void refreshAttendance();
      },
      onError: (err) =>
        toast({
          title: "Could not log out",
          description: errorMessage(err, "Please try again."),
          variant: "destructive",
        }),
    },
  });

  const toggleActive = (u: User) =>
    setActive.mutate({ id: u.id, data: { active: !u.active } });

  const openEdit = (u: User) => {
    setEditing(u);
    setEditName(u.name);
    setEditNumber(u.whatsappNumber);
    setEditRole(u.role as UserRole);
    setEditSpaceId(u.spaceId == null ? NO_SPACE : String(u.spaceId));
    setEditBiometricId(u.biometricId ?? "");
    setEditBiometricSpaceId(u.biometricSpaceId == null ? NO_SPACE : String(u.biometricSpaceId));
  };

  const submitInvite = () => {
    if (!inviteName.trim() || !inviteNumber.trim()) {
      toast({ title: "Name and WhatsApp number are required.", variant: "destructive" });
      return;
    }
    if (!isTenDigits(inviteNumber)) {
      toast({ title: PHONE_HELP, variant: "destructive" });
      return;
    }
    if (SPACE_REQUIRED_ROLES.has(inviteRole) && inviteSpaceId === NO_SPACE) {
      toast({
        title: "Pick a space",
        description: "Agents and Centralised Team members must belong to a space.",
        variant: "destructive",
      });
      return;
    }
    if (inviteBiometricId.trim() && inviteBiometricSpaceId === NO_SPACE) {
      toast({
        title: "Pick the biometric location",
        description: "Choose which site's machine this Biometric ID is on.",
        variant: "destructive",
      });
      return;
    }
    invite.mutate({
      data: {
        name: inviteName.trim(),
        whatsappNumber: inviteNumber.trim(),
        role: inviteRole,
        spaceId: inviteSpaceId === NO_SPACE ? null : Number(inviteSpaceId),
        biometricId: inviteBiometricId.trim() || null,
        biometricSpaceId: inviteBiometricSpaceId === NO_SPACE ? null : Number(inviteBiometricSpaceId),
      },
    });
  };

  const submitEdit = () => {
    if (!editing) return;
    if (!editName.trim()) {
      toast({ title: "Name is required.", variant: "destructive" });
      return;
    }
    if (!isTenDigits(editNumber)) {
      toast({ title: PHONE_HELP, variant: "destructive" });
      return;
    }
    if (SPACE_REQUIRED_ROLES.has(editRole) && editSpaceId === NO_SPACE) {
      toast({
        title: "Pick a space",
        description: "Agents and Centralised Team members must belong to a space.",
        variant: "destructive",
      });
      return;
    }
    if (editBiometricId.trim() && editBiometricSpaceId === NO_SPACE) {
      toast({
        title: "Pick the biometric location",
        description: "Choose which site's machine this Biometric ID is on.",
        variant: "destructive",
      });
      return;
    }
    update.mutate({
      id: editing.id,
      data: {
        name: editName.trim(),
        whatsappNumber: editNumber.trim(),
        role: editRole,
        spaceId: editSpaceId === NO_SPACE ? null : Number(editSpaceId),
        biometricId: editBiometricId.trim() || null,
        biometricSpaceId: editBiometricSpaceId === NO_SPACE ? null : Number(editBiometricSpaceId),
      },
    });
  };

  const inviteRoleNeedsSpace = SPACE_REQUIRED_ROLES.has(inviteRole);
  const editRoleNeedsSpace = SPACE_REQUIRED_ROLES.has(editRole);

  if (!meLoading && me && !isAdmin) {
    return (
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Team</h1>
        <p className="text-muted-foreground">
          You don't have permission to manage team members.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Invite teammates, change their role, and assign them to a space.
          </p>
        </div>
        <Button onClick={() => setShowInvite(true)} data-testid="invite-member-btn">
          <Plus className="mr-2 h-4 w-4" />
          Invite Member
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <p className="text-sm text-muted-foreground">
            Login is captured automatically from the biometric punch-in. There is no punch-out, so
            logout is manual — hours are counted from login to the logout you record.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Space</TableHead>
                <TableHead>Login</TableHead>
                <TableHead>Logout</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead className="w-56 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-14" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : users?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                    No team members yet.
                  </TableCell>
                </TableRow>
              ) : (
                users?.map((u) => {
                  const att = attendanceByUser.get(u.id);
                  const minutes = att ? dayMinutes(att, nowMs) : null;
                  return (
                  <TableRow
                    key={u.id}
                    data-testid={`team-row-${u.id}`}
                    className={u.active ? undefined : "opacity-60"}
                  >
                    <TableCell className="font-medium">
                      {u.name}
                      {me?.id === u.id && (
                        <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                      )}
                      {!u.active && (
                        <Badge variant="outline" className="ml-2 text-muted-foreground">
                          Disabled
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{u.whatsappNumber}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{ROLE_LABEL[u.role] ?? u.role}</Badge>
                    </TableCell>
                    <TableCell>{spaceLabel(u.spaceId, spaces)}</TableCell>
                    <TableCell className="tabular-nums" data-testid={`login-at-${u.id}`}>
                      {att ? (
                        <span className="whitespace-nowrap">{fmtTime(att.entryAt)}</span>
                      ) : u.biometricId ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not enrolled</span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {att?.loggedIn ? (
                        <Badge variant="outline" className="text-amber-600">Pending</Badge>
                      ) : att?.exitAt ? (
                        fmtTime(att.exitAt)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums" data-testid={`hours-${u.id}`}>
                      {minutes == null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className={att?.loggedIn ? "text-muted-foreground" : undefined}>
                          {fmtDuration(minutes)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {att?.loggedIn && att.openSessionId != null && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mr-1 h-7"
                          onClick={() => setLoggingOut({ user: u, entry: att })}
                          data-testid={`logout-member-${u.id}`}
                        >
                          <LogOut className="mr-1 h-3.5 w-3.5" />
                          Logout
                        </Button>
                      )}
                      {/* You can't disable yourself — server enforces this too. */}
                      {me?.id !== u.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleActive(u)}
                          disabled={setActive.isPending}
                          className={u.active ? "text-destructive hover:text-destructive" : undefined}
                          data-testid={`toggle-active-${u.id}`}
                        >
                          {u.active ? "Disable" : "Enable"}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(u)}
                        data-testid={`edit-member-${u.id}`}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Invite dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite team member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                placeholder="Jane Doe"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                data-testid="invite-name"
              />
            </div>
            <div>
              <Label>WhatsApp number</Label>
              <Input
                placeholder="10-digit mobile (no +91, no spaces)"
                inputMode="numeric"
                maxLength={10}
                value={inviteNumber}
                onChange={(e) => setInviteNumber(normalizePhone(e.target.value))}
                data-testid="invite-number"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The user signs in by requesting a WhatsApp OTP to this number.
              </p>
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as UserRole)}
              >
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Space {inviteRoleNeedsSpace && <span className="text-destructive">*</span>}</Label>
              <Select
                value={inviteSpaceId}
                onValueChange={setInviteSpaceId}
                disabled={spacesLoading}
              >
                <SelectTrigger data-testid="invite-space">
                  <SelectValue placeholder="Select a space" />
                </SelectTrigger>
                <SelectContent>
                  {!inviteRoleNeedsSpace && (
                    <SelectItem value={NO_SPACE}>None (global access)</SelectItem>
                  )}
                  {spaces?.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name} — {s.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {inviteRoleNeedsSpace && (
                <p className="text-xs text-muted-foreground mt-1">
                  Required for Agents and Centralised Team members.
                </p>
              )}
            </div>
            <div>
              <Label>Biometric ID (optional)</Label>
              <Input
                placeholder="Device User ID for attendance"
                value={inviteBiometricId}
                onChange={(e) => setInviteBiometricId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                This person's enrollment ID on the biometric device — used to match their attendance punches.
              </p>
            </div>
            <div>
              <Label>
                Biometric location {inviteBiometricId.trim() && <span className="text-destructive">*</span>}
              </Label>
              <Select
                value={inviteBiometricSpaceId}
                onValueChange={setInviteBiometricSpaceId}
                disabled={spacesLoading}
              >
                <SelectTrigger><SelectValue placeholder="Which site's machine?" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SPACE}>None</SelectItem>
                  {spaces?.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name} — {s.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Which location's biometric machine this ID is on (required when a Biometric ID is set, especially for global-access users).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button
              onClick={submitInvite}
              disabled={invite.isPending}
              data-testid="invite-submit"
            >
              {invite.isPending ? "Inviting…" : "Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editing != null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                placeholder="Full name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                data-testid="edit-name"
              />
            </div>
            <div>
              <Label>WhatsApp number</Label>
              <Input
                placeholder="10-digit mobile (no +91, no spaces)"
                inputMode="numeric"
                maxLength={10}
                value={editNumber}
                onChange={(e) => setEditNumber(normalizePhone(e.target.value))}
                data-testid="edit-number"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This is the number they request a WhatsApp OTP to when signing in. Must be unique.
              </p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editRole} onValueChange={(v) => setEditRole(v as UserRole)}>
                <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Space {editRoleNeedsSpace && <span className="text-destructive">*</span>}</Label>
              <Select
                value={editSpaceId}
                onValueChange={setEditSpaceId}
                disabled={spacesLoading}
              >
                <SelectTrigger data-testid="edit-space">
                  <SelectValue placeholder="Select a space" />
                </SelectTrigger>
                <SelectContent>
                  {!editRoleNeedsSpace && (
                    <SelectItem value={NO_SPACE}>None (global access)</SelectItem>
                  )}
                  {spaces?.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name} — {s.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!editRoleNeedsSpace && (
                <p className="text-xs text-muted-foreground mt-1">
                  Super Admin and Centralised Admin can see every space, so a space assignment is optional.
                </p>
              )}
            </div>
            <div>
              <Label>Biometric ID (optional)</Label>
              <Input
                placeholder="Device User ID for attendance"
                value={editBiometricId}
                onChange={(e) => setEditBiometricId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enrollment ID on the biometric device — used to match attendance punches.
              </p>
            </div>
            <div>
              <Label>
                Biometric location {editBiometricId.trim() && <span className="text-destructive">*</span>}
              </Label>
              <Select
                value={editBiometricSpaceId}
                onValueChange={setEditBiometricSpaceId}
                disabled={spacesLoading}
              >
                <SelectTrigger><SelectValue placeholder="Which site's machine?" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SPACE}>None</SelectItem>
                  {spaces?.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name} — {s.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Which location's biometric machine this ID is on (required when a Biometric ID is set).
              </p>
            </div>
            {me?.id === editing?.id && (
              <p className="text-xs text-amber-600">
                Heads up: you are editing your own account. Changing your role may immediately revoke access.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              onClick={submitEdit}
              disabled={update.isPending}
              data-testid="edit-submit"
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual logout — closes the biometric login and banks the hours */}
      {loggingOut && (
        <LogoutDialog
          user={loggingOut.user}
          entry={loggingOut.entry}
          pending={logout.isPending}
          onCancel={() => setLoggingOut(null)}
          onConfirm={(exitAt) =>
            logout.mutate({ id: loggingOut.entry.openSessionId!, data: { exitAt } })
          }
        />
      )}
    </div>
  );
}

// ── Logout dialog ───────────────────────────────────────────────────────────
// The exit time is editable because a manual logout is often recorded after the
// fact ("she left at 6, it's now 8"). Hours shown here are what the server will
// compute and store — same login → logout arithmetic.
function LogoutDialog({
  user,
  entry,
  pending,
  onCancel,
  onConfirm,
}: {
  user: User;
  entry: StaffAttendanceDayEntry;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (exitAt: string) => void;
}) {
  const { toast } = useToast();
  const [exitLocal, setExitLocal] = useState(() => toLocalInput(new Date().toISOString()));

  const exitISO = localInputToISO(exitLocal);
  const loginMs = entry.openEntryAt ? new Date(entry.openEntryAt).getTime() : NaN;
  const exitMs = exitISO ? new Date(exitISO).getTime() : NaN;
  const sessionMinutes =
    Number.isNaN(loginMs) || Number.isNaN(exitMs) || exitMs < loginMs
      ? null
      : Math.round((exitMs - loginMs) / 60_000);
  const bankedEarlier = entry.durationMinutes ?? 0;

  const submit = () => {
    if (!exitISO) {
      toast({ title: "Enter a logout time", variant: "destructive" });
      return;
    }
    if (sessionMinutes == null) {
      toast({ title: "Logout time cannot be before the login time.", variant: "destructive" });
      return;
    }
    if (exitMs > Date.now() + 60_000) {
      toast({ title: "Logout time cannot be in the future.", variant: "destructive" });
      return;
    }
    onConfirm(exitISO);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log out {user.name}</DialogTitle>
          <DialogDescription>
            The biometric punch only records the login, so the hours are counted from it up to the
            logout time you set here.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
            <span className="text-muted-foreground">Login (auto-detected)</span>
            <span className="font-medium tabular-nums">{fmtTime(entry.openEntryAt)}</span>
          </div>
          <div>
            <Label>Logout time</Label>
            <Input
              type="datetime-local"
              value={exitLocal}
              onChange={(e) => setExitLocal(e.target.value)}
              data-testid="logout-exit-at"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Defaults to now. Back-date it if the person has already left.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Hours counted</span>
            <span className="font-medium tabular-nums" data-testid="logout-hours-preview">
              {sessionMinutes == null ? "—" : fmtDuration(sessionMinutes)}
            </span>
          </div>
          {bankedEarlier > 0 && sessionMinutes != null && (
            <p className="text-xs text-muted-foreground">
              Earlier sessions today already count {fmtDuration(bankedEarlier)} — the day totals{" "}
              {fmtDuration(bankedEarlier + sessionMinutes)}.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={pending} data-testid="logout-submit">
            {pending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="mr-1.5 h-4 w-4" />
            )}
            {pending ? "Logging out…" : "Log out & save hours"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
